import { useState, useEffect, useCallback, useRef } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import InstitutionTags from "components/admin/InstitutionTags";
import { adminGet, adminPut, adminPostForm } from "lib/adminClient";
import { mediaUrl } from "lib/media";

/**
 * Editor for the admissions page's two practical resources: the fee payment
 * walkthrough and the downloadable forms.
 *
 * Both are scoped with the site's usual institution tags — no tags means it
 * shows on every institution's admissions page, so the school can run one
 * shared payment guide or a different one per institution without a developer.
 *
 * The whole blob is replaced on save, so a failed load must block saving:
 * otherwise an empty screen would overwrite real content with nothing.
 */

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";
const addBtnCls =
  "bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90";
const delBtnCls = "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]";
const moveBtnCls = "bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs";

/** Uploads live at the API origin; files shipped in public/ are served by the site. */
const srcFor = (p) => (p?.startsWith("/uploads/") ? mediaUrl(p) : p);

const blankGuide = () => ({
  title: "",
  titleEn: "",
  billerId: "",
  identifierBn: "",
  identifierEn: "",
  noteBn: "",
  noteEn: "",
  tags: [],
  steps: [],
});

export default function AdmissionResourcesPage() {
  const [guides, setGuides] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/admission-resources")) || {};
      setGuides(d.paymentGuides || []);
      setForms(d.forms || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ------------------------------------------------------------- guides -- */

  const updGuide = (gi, field, value) =>
    setGuides((gs) => gs.map((g, i) => (i === gi ? { ...g, [field]: value } : g)));

  const updStep = (gi, si, field, value) =>
    setGuides((gs) =>
      gs.map((g, i) =>
        i === gi
          ? { ...g, steps: g.steps.map((s, j) => (j === si ? { ...s, [field]: value } : s)) }
          : g
      )
    );

  const moveStep = (gi, si, dir) =>
    setGuides((gs) =>
      gs.map((g, i) => {
        if (i !== gi) return g;
        const j = si + dir;
        if (j < 0 || j >= g.steps.length) return g;
        const steps = [...g.steps];
        [steps[si], steps[j]] = [steps[j], steps[si]];
        return { ...g, steps };
      })
    );

  const delStep = (gi, si) =>
    setGuides((gs) =>
      gs.map((g, i) => (i === gi ? { ...g, steps: g.steps.filter((_, j) => j !== si) } : g))
    );

  const uploadStepImage = async (gi, si, file) => {
    if (!file) return;
    const key = `g${gi}s${si}`;
    setBusy(key);
    setError("");
    try {
      const form = new FormData();
      form.append("image", file);
      const { url } = await adminPostForm("/admin/media", form);
      updStep(gi, si, "image", url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  /* -------------------------------------------------------------- forms -- */

  const updForm = (fi, field, value) =>
    setForms((fs) => fs.map((f, i) => (i === fi ? { ...f, [field]: value } : f)));

  const uploadForm = async (fi, file) => {
    if (!file) return;
    const key = `f${fi}`;
    setBusy(key);
    setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      const { url } = await adminPostForm("/admin/media/document", data);
      updForm(fi, "file", url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  /* --------------------------------------------------------------- save -- */

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const payload = {
        // A guide with no usable step would render an empty panel on the site.
        paymentGuides: guides
          .map((g) => ({ ...g, steps: (g.steps || []).filter((s) => (s.image || "").trim()) }))
          .filter((g) => g.steps.length),
        forms: forms.filter((f) => (f.label || "").trim() && (f.file || "").trim()),
      };
      const saved = await adminPut("/admin/content/admission-resources", payload);
      if (saved) {
        setGuides(saved.paymentGuides || []);
        setForms(saved.forms || []);
      }
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}
      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      {/* ------------------------------------------------ payment guides -- */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Payment procedure"
          action={
            <button
              type="button"
              className={addBtnCls}
              onClick={() => setGuides((gs) => [...gs, blankGuide()])}
            >
              + Add guide
            </button>
          }
        />
        <div className="p-[22px]">
          <p className="text-muted text-[12.5px] mb-4">
            Shown on the admissions page as an animated walkthrough. Leave the institutions empty
            to show the same guide everywhere, or tag a guide to give one institution its own.
          </p>

          {guides.length === 0 && (
            <p className="text-muted text-sm italic">No payment guide yet.</p>
          )}

          {guides.map((g, gi) => (
            <div key={gi} className="border-[1.5px] border-line rounded-xl p-4 mb-4">
              <div className="flex justify-between items-start gap-3 mb-3 flex-wrap">
                <InstitutionTags
                  value={g.tags || []}
                  onChange={(tags) => updGuide(gi, "tags", tags)}
                />
                <button
                  type="button"
                  className={delBtnCls}
                  onClick={() => setGuides((gs) => gs.filter((_, i) => i !== gi))}
                >
                  Remove guide
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">Title (Bangla)</span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={g.title || ""}
                    onChange={(e) => updGuide(gi, "title", e.target.value)}
                    placeholder="রকেটে ভর্তি ফি পরিশোধ"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">Title (English)</span>
                  <input
                    className={inputCls}
                    value={g.titleEn || ""}
                    onChange={(e) => updGuide(gi, "titleEn", e.target.value)}
                    placeholder="Paying the admission fee with Rocket"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">Biller ID</span>
                  <input
                    className={inputCls}
                    value={g.billerId || ""}
                    onChange={(e) => updGuide(gi, "billerId", e.target.value)}
                    placeholder="5391"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    What the app asks for (Bangla)
                  </span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={g.identifierBn || ""}
                    onChange={(e) => updGuide(gi, "identifierBn", e.target.value)}
                    placeholder="এসএসসি রোল"
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">
                    What the app asks for (English)
                  </span>
                  <input
                    className={inputCls}
                    value={g.identifierEn || ""}
                    onChange={(e) => updGuide(gi, "identifierEn", e.target.value)}
                    placeholder="SSC Roll"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">Footnote (Bangla)</span>
                  <input
                    className={`${inputCls} font-bn`}
                    value={g.noteBn || ""}
                    onChange={(e) => updGuide(gi, "noteBn", e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-muted">Footnote (English)</span>
                  <input
                    className={inputCls}
                    value={g.noteEn || ""}
                    onChange={(e) => updGuide(gi, "noteEn", e.target.value)}
                  />
                </label>
              </div>

              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold text-muted">
                  Steps ({(g.steps || []).length})
                </span>
                <button
                  type="button"
                  className={addBtnCls}
                  onClick={() => updGuide(gi, "steps", [...(g.steps || []), {}])}
                >
                  + Add step
                </button>
              </div>

              {(g.steps || []).map((s, si) => (
                <div
                  key={si}
                  className="flex gap-3 items-start border border-line rounded-lg p-3 mb-2"
                >
                  <div className="shrink-0 text-center">
                    <div className="w-[64px] h-[132px] rounded-md border border-line bg-[#f7f8f8] overflow-hidden flex items-center justify-center">
                      {s.image ? (
                        <img src={srcFor(s.image)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[10px] text-muted">No image</span>
                      )}
                    </div>
                    <input
                      ref={(el) => (fileRefs.current[`g${gi}s${si}`] = el)}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadStepImage(gi, si, e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      className={`${moveBtnCls} mt-1.5 w-full`}
                      disabled={busy === `g${gi}s${si}`}
                      onClick={() => fileRefs.current[`g${gi}s${si}`]?.click()}
                    >
                      {busy === `g${gi}s${si}` ? "…" : s.image ? "Replace" : "Upload"}
                    </button>
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <span className="text-[12px] font-semibold text-muted">Step {si + 1}</span>
                    <input
                      className={`${inputCls} font-bn`}
                      value={s.bn || ""}
                      onChange={(e) => updStep(gi, si, "bn", e.target.value)}
                      placeholder="বাংলা নির্দেশনা"
                    />
                    <input
                      className={inputCls}
                      value={s.en || ""}
                      onChange={(e) => updStep(gi, si, "en", e.target.value)}
                      placeholder="English instruction"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button type="button" className={moveBtnCls} onClick={() => moveStep(gi, si, -1)}>
                      ↑
                    </button>
                    <button type="button" className={moveBtnCls} onClick={() => moveStep(gi, si, 1)}>
                      ↓
                    </button>
                    <button type="button" className={delBtnCls} onClick={() => delStep(gi, si)}>
                      ✕
                    </button>
                  </div>
                </div>
              ))}

              {!(g.steps || []).length && (
                <p className="text-muted text-[12.5px] italic">
                  A guide with no steps is not shown on the site.
                </p>
              )}
            </div>
          ))}
        </div>
      </AdminCard>

      {/* ------------------------------------------------ downloadable forms */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Downloadable forms"
          action={
            <button
              type="button"
              className={addBtnCls}
              onClick={() => setForms((fs) => [...fs, { label: "", file: "", tags: [] }])}
            >
              + Add form
            </button>
          }
        />
        <div className="p-[22px]">
          <p className="text-muted text-[12.5px] mb-4">
            PDFs listed under “Documents and guidance”. Uploaded as-is, never re-encoded.
          </p>

          {forms.length === 0 && <p className="text-muted text-sm italic">No forms yet.</p>}

          {forms.map((f, fi) => (
            <div key={fi} className="border-[1.5px] border-line rounded-xl p-4 mb-3">
              <div className="flex gap-3 items-start flex-wrap">
                <label className="block flex-1 min-w-[200px]">
                  <span className="text-[12px] font-semibold text-muted">Link text</span>
                  <input
                    className={inputCls}
                    value={f.label || ""}
                    onChange={(e) => updForm(fi, "label", e.target.value)}
                    placeholder="Blank form: page 1"
                  />
                </label>
                <div className="flex-1 min-w-[200px]">
                  <span className="text-[12px] font-semibold text-muted block mb-1">File</span>
                  <div className="flex items-center gap-2">
                    <input
                      ref={(el) => (fileRefs.current[`f${fi}`] = el)}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => uploadForm(fi, e.target.files?.[0])}
                    />
                    <button
                      type="button"
                      className={moveBtnCls}
                      disabled={busy === `f${fi}`}
                      onClick={() => fileRefs.current[`f${fi}`]?.click()}
                    >
                      {busy === `f${fi}` ? "Uploading…" : f.file ? "Replace PDF" : "Upload PDF"}
                    </button>
                    {f.file && (
                      <a
                        href={srcFor(f.file)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[12px] text-green underline truncate"
                      >
                        {f.file.split("/").pop()}
                      </a>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className={delBtnCls}
                  onClick={() => setForms((fs) => fs.filter((_, i) => i !== fi))}
                >
                  Remove
                </button>
              </div>
              <div className="mt-3">
                <InstitutionTags value={f.tags || []} onChange={(tags) => updForm(fi, "tags", tags)} />
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-lg px-5 py-2.5 font-sans font-semibold text-sm cursor-pointer disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        {status && <span className="text-[13px] text-green font-semibold">{status}</span>}
      </div>
    </div>
  );
}
